import React from "react";
import cn from "classnames";

/**
 * A banner's baked background (libs/bakedBg.ts): an image already cut to the
 * background box, shown at the full box size. It has no transform and no
 * transition.
 *
 * The markup keeps the classes the page stylesheets and the renderer rely on:
 * `.bg-transformer` (per-template overlays, the grid), `.image-wrapper`, and
 * `img[alt='background']` (the renderer's background-ready probe).
 * Brightness, blur and opacity stay live CSS, as on a positioned background.
 */
export interface BakedImageProps {
  src: string;
  /** The crop scale the image was cut at; blur scales with it (see BakedBgEntry). */
  cropScale: number;
  brightnessPercent: number;
  opacityPercent: number;
  blur: number;
  /**
   * Shown under the positioned image while that image loads: not the
   * banner's background for the renderer's probe, and out of the layout.
   */
  underlay?: boolean;
}

/** The cut image in its `.image-wrapper`, for a `.bg-transformer` box. */
export function BakedImage({
  src,
  cropScale,
  brightnessPercent,
  opacityPercent,
  blur,
  underlay = false,
}: BakedImageProps) {
  return (
    <div
      className={underlay ? undefined : "image-wrapper"}
      style={{
        overflow: "hidden",
        position: underlay ? "absolute" : "relative",
        ...(underlay && {inset: 0}),
        pointerEvents: "none",
      }}
    >
      <img
        src={src}
        alt={underlay ? "" : "background"}
        aria-hidden={underlay || undefined}
        className="bg-image"
        draggable={false}
        crossOrigin="anonymous"
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          filter: `brightness(${brightnessPercent}%) blur(${blur * cropScale}px)`,
          opacity: opacityPercent / 100,
        }}
      />
    </div>
  );
}

export interface BakedBackgroundProps extends Omit<BakedImageProps, "underlay"> {
  id: string;
  showGrid: boolean;
  gridStyle: React.CSSProperties;
}

/**
 * A baked ready cut (BgTransformer/templateBg.ts): the cut, with no
 * positioning controls. A baked banner of any other image is a
 * BgTransformer, which shows the cut and keeps the controls.
 */
export default function BakedBackground({id, showGrid, gridStyle, ...image}: BakedBackgroundProps) {
  return (
    <div
      className={cn("bg-transformer", {nogrid: !showGrid})}
      data-id={id}
      data-baked-bg=""
      style={gridStyle}
    >
      <BakedImage {...image} />
    </div>
  );
}
